import { CenterSpin } from '@/components/CenterSpin';
import { CreatePipelineModal } from '@/components/PipelineModals';
import {
  DetailsPageLayout,
  type DetailsPageLayoutProps,
} from '@/layouts/DetailsPageLayout';
import {
  usePipelineById,
  usePipelineConfigurationVersionsByPipelineId,
  usePipelineExecutionsByPipelineId,
  useUpdatePipeline,
} from '@/queries/pipelines';
import { formatRelativeTime, getErrorMessage } from '@/utils';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import ExperimentOutlined from '@ant-design/icons/ExperimentOutlined';
import HistoryOutlined from '@ant-design/icons/HistoryOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import ShareAltOutlined from '@ant-design/icons/ShareAltOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Input from 'antd/es/input';
import Space from 'antd/es/space';
import Table from 'antd/es/table';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { DetailRow, Section, VersionHistoryButton } from 'ui-components';

const { Title, Text, Paragraph } = Typography;

const SectionContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.marginLG}px;
`;

const UppercaseLabel = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const DescriptionContainer = styled.div`
  margin-top: ${({ theme }) => theme.marginXS}px;
`;

const DescriptionEditContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const DescriptionText = styled(Paragraph)`
  margin: 0;
  flex: 1;
`;

const EditIcon = styled(EditOutlined)`
  color: ${({ theme }) => theme.colorTextTertiary};
  font-size: ${({ theme }) => theme.fontSize}px;
  margin-top: ${({ theme }) => theme.marginXXS}px;
`;

const EmptyDescriptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const ConfigSection = styled(Section)`
  background: ${({ theme }) => theme.colorFillAlter};
  margin-top: ${({ theme }) => theme.marginXS}px;
`;

const EmptyStateSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.paddingXL}px 0;
`;

const EmptyStateTitle = styled(Title)`
  margin: 0 0 ${({ theme }) => theme.marginXS}px 0 !important;
`;

const EmptyStateDescription = styled(Text)`
  display: block;
  margin-bottom: ${({ theme }) => theme.margin}px;
`;

const EditActionsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.marginXS}px;
  align-items: center;
  flex-shrink: 0;
  margin-top: ${({ theme }) => theme.marginSM}px;

  .ant-btn {
    min-height: ${({ theme }) => theme.controlHeight}px;
    display: flex;
    align-items: center;
    justify-content: center;

    &.ant-btn-sm {
      min-height: ${({ theme }) => theme.controlHeightSM}px;
      padding: ${({ theme }) => theme.paddingXXS}px
        ${({ theme }) => theme.paddingXS}px;
    }
  }
`;

export const PipelineDetailPage: React.FC = () => {
  const { teamId, pipelineId } = useParams<{
    teamId: string;
    pipelineId: string;
  }>();
  const { message } = App.useApp();
  const navigate = useNavigate();

  const {
    data: pipeline,
    isLoading: isPipelineLoading,
    error: pipelineError,
  } = usePipelineById(pipelineId || null, {
    enabled: !!pipelineId,
  });

  const { data: configVersions, isLoading: isConfigVersionsLoading } =
    usePipelineConfigurationVersionsByPipelineId(pipelineId || null, {
      enabled: !!pipelineId,
    });

  const { data: executions, isLoading: isExecutionsLoading } =
    usePipelineExecutionsByPipelineId(pipelineId || null, {
      enabled: !!pipelineId,
    });

  const updatePipelineMutation = useUpdatePipeline();

  // Edit states
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Get current configuration version
  const currentConfigVersion = configVersions?.items.find(
    (version) => version.status === 'current',
  );

  // Get recent executions (limit to 5 for detail view)
  const recentExecutions = executions?.items.slice(0, 5) || [];

  const handleSharePipeline = useCallback(async () => {
    try {
      const currentUrl = window.location.href;
      await navigator.clipboard.writeText(currentUrl);
      void message.success('Pipeline URL copied to clipboard');
    } catch (error) {
      void message.error('Failed to copy URL to clipboard');
    }
  }, [message]);

  const handleEditPipeline = useCallback(() => {
    // TODO: Implement edit pipeline functionality
    void message.info('Edit pipeline functionality will be implemented soon');
  }, [message]);

  // Edit handlers
  const startEditingDescription = () => {
    if (pipeline) {
      setEditedDescription(pipeline.description || '');
      setIsEditingDescription(true);
    }
  };

  const cancelEditingDescription = () => {
    setIsEditingDescription(false);
    setEditedDescription('');
  };

  const saveDescriptionEdit = async () => {
    if (!pipeline || !pipelineId) return;

    try {
      await updatePipelineMutation.mutateAsync({
        pipelineId,
        data: { description: editedDescription.trim() || null },
      });
      setIsEditingDescription(false);
      void message.success('Pipeline description updated successfully');
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to update pipeline description',
      );
      void message.error(errorMessage);
    }
  };

  const getExecutionStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
        return 'processing';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  }, []);

  // Memoized execution columns
  const executionColumns = useMemo(
    () => [
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => (
          <Tag color={getExecutionStatusColor(status)}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Tag>
        ),
      },
      {
        title: 'Started',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 120,
        render: (date: Date) => formatRelativeTime(date),
      },
      {
        title: 'Duration',
        key: 'duration',
        width: 100,
        render: (record: {
          startedAt?: Date;
          completedAt?: Date;
          failedAt?: Date;
          cancelledAt?: Date;
        }) => {
          if (!record.startedAt) return '—';
          const endTime =
            record.completedAt ||
            record.failedAt ||
            record.cancelledAt ||
            new Date();
          const duration =
            new Date(endTime).getTime() - new Date(record.startedAt).getTime();
          const minutes = Math.floor(duration / (1000 * 60));
          const seconds = Math.floor((duration % (1000 * 60)) / 1000);
          return `${minutes}m ${seconds}s`;
        },
      },
      {
        title: 'Input Summary',
        dataIndex: 'pipelineInputSummary',
        key: 'pipelineInputSummary',
        ellipsis: true,
      },
    ],
    [getExecutionStatusColor],
  );

  // Copy the pipeline id to the clipboard
  const handleCopyId = async () => {
    if (!pipeline) return;
    try {
      await navigator.clipboard.writeText(pipeline.pipelineId);
      void message.success('ID copied to clipboard');
    } catch (error) {
      void message.error('Failed to copy ID');
    }
  };

  // Determine layout props based on state
  const layoutProps: DetailsPageLayoutProps = useMemo(() => {
    if (!teamId || !pipelineId) {
      return {
        title: 'Missing Information',
        breadcrumbs: [
          {
            label: 'Pipelines',
            path: teamId ? `/team/${teamId}/pipelines` : '/',
          },
          { label: 'Error' },
        ],
      };
    }

    if (pipelineError) {
      return {
        title: 'Error Loading Pipeline',
        breadcrumbs: [
          { label: 'Pipelines', path: `/team/${teamId}/pipelines` },
          { label: 'Error' },
        ],
      };
    }

    if (!pipeline) {
      return {
        title: 'Pipeline Not Found',
        breadcrumbs: [
          { label: 'Pipelines', path: `/team/${teamId}/pipelines` },
          { label: 'Not Found' },
        ],
      };
    }

    return {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {pipeline.name}
        </div>
      ),
      breadcrumbs: [
        { label: 'Pipelines', path: `/team/${teamId}/pipelines` },
        { label: pipeline.name },
      ],
      actions: [
        {
          label: 'Workbench',
          type: 'primary' as const,
          icon: <ExperimentOutlined />,
          onClick: () => {
            void navigate(`/team/${teamId}/pipelines/${pipelineId}/workbench`);
          },
        },
        {
          label: 'Share pipeline',
          type: 'secondary' as const,
          icon: <ShareAltOutlined />,
          onClick: handleSharePipeline,
        },
        {
          type: 'secondary' as const,
          icon: <EditOutlined />,
          label: 'Edit details',
          onClick: handleEditPipeline,
        },
        {
          type: 'danger' as const,
          icon: <DeleteOutlined />,
          label: 'Delete pipeline',
          danger: true,
          divided: true,
          onClick: () => {
            void message.warning(
              'Delete pipeline functionality will be implemented soon',
            );
          },
        },
      ],
    };
  }, [
    teamId,
    pipelineId,
    pipelineError,
    pipeline,
    handleSharePipeline,
    handleEditPipeline,
    navigate,
    message,
  ]);

  // Render different content based on state
  const renderContent = () => {
    if (!teamId || !pipelineId) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Text type="danger">
              Unable to load pipeline details. Team or pipeline information is
              missing.
            </Text>
            <div style={{ marginTop: 16 }}>
              <Button
                type="primary"
                onClick={() => {
                  window.location.href = teamId
                    ? `/team/${teamId}/pipelines`
                    : '/';
                }}
              >
                Back to Pipelines
              </Button>
            </div>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    if (isPipelineLoading || isConfigVersionsLoading || isExecutionsLoading) {
      return (
        <DetailsPageLayout.TopContent>
          <CenterSpin />
        </DetailsPageLayout.TopContent>
      );
    }

    if (pipelineError) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Text type="danger">
              There was an error loading the pipeline details. Please try again.
            </Text>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    if (!pipeline) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Text type="secondary">
              The pipeline you're looking for could not be found.
            </Text>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    // Render success state with side content and main content
    return [
      <DetailsPageLayout.SideContent
        key="side-content"
        title="Pipeline Overview"
      >
        <DetailRow>
          <Text>Pipeline ID</Text>
          <Tooltip title="Click to copy ID">
            <Text code style={{ cursor: 'pointer' }} onClick={handleCopyId}>
              {pipeline.pipelineId}
            </Text>
          </Tooltip>
        </DetailRow>
        <DetailRow>
          <Text>Created</Text>
          <Text>{formatRelativeTime(pipeline.createdAt)}</Text>
        </DetailRow>
        <DetailRow>
          <Text>Last Updated</Text>
          <Text>{formatRelativeTime(pipeline.updatedAt)}</Text>
        </DetailRow>
        <DetailRow>
          <Text>Last Run</Text>
          <Text>
            {pipeline.lastRunAt
              ? formatRelativeTime(pipeline.lastRunAt)
              : 'Never'}
          </Text>
        </DetailRow>
        <DetailRow>
          <Text>Total Runs</Text>
          <Text>{pipeline.totalRuns.toLocaleString()}</Text>
        </DetailRow>
        <DetailRow>
          <Text>Successful Runs</Text>
          <Text>{pipeline.successfulRuns.toLocaleString()}</Text>
        </DetailRow>
        <DetailRow>
          <Text>Success Rate</Text>
          <Text>
            {pipeline.totalRuns > 0
              ? `${Math.round(pipeline.successRate)}%`
              : '—'}
          </Text>
        </DetailRow>
      </DetailsPageLayout.SideContent>,

      <DetailsPageLayout.MainContent
        key="main-content-details"
        title="Pipeline details"
        extra={
          currentConfigVersion && (
            <VersionHistoryButton
              icon={<HistoryOutlined />}
              onClick={() =>
                message.info('Version history will be implemented soon')
              }
            >
              Version History
            </VersionHistoryButton>
          )
        }
      >
        <SectionContainer>
          <UppercaseLabel type="secondary">ABOUT THIS PIPELINE</UppercaseLabel>
          <DescriptionContainer>
            {isEditingDescription ? (
              <div>
                <Input.TextArea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      void saveDescriptionEdit();
                    }
                  }}
                  placeholder="Enter a description for this pipeline..."
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  autoFocus
                />
                <EditActionsContainer>
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={saveDescriptionEdit}
                    loading={updatePipelineMutation.isPending}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={cancelEditingDescription}
                  >
                    Cancel
                  </Button>
                </EditActionsContainer>
              </div>
            ) : (
              <div
                style={{ cursor: 'pointer' }}
                onClick={startEditingDescription}
              >
                {pipeline.description ? (
                  <DescriptionEditContainer>
                    <DescriptionText>{pipeline.description}</DescriptionText>
                    <EditIcon />
                  </DescriptionEditContainer>
                ) : (
                  <EmptyDescriptionContainer>
                    <Text type="secondary" italic>
                      No description provided - click to add one
                    </Text>
                    <EditIcon />
                  </EmptyDescriptionContainer>
                )}
              </div>
            )}
          </DescriptionContainer>
        </SectionContainer>

        <SectionContainer>
          <UppercaseLabel type="secondary">CONFIGURATION</UppercaseLabel>
          {currentConfigVersion ? (
            <ConfigSection>
              <DetailRow>
                <Text>CURRENT VERSION</Text>
                <Text>v{currentConfigVersion.version}</Text>
              </DetailRow>
              <DetailRow>
                <Text>STATUS</Text>
                <Tag color="success">Current</Tag>
              </DetailRow>
              <DetailRow>
                <Text>UPDATED</Text>
                <Text>
                  {formatRelativeTime(currentConfigVersion.updatedAt)}
                </Text>
              </DetailRow>
            </ConfigSection>
          ) : (
            <ConfigSection>
              <EmptyStateSection>
                <EmptyStateTitle level={5}>
                  No configuration versions
                </EmptyStateTitle>
                <EmptyStateDescription type="secondary">
                  Create your first configuration to start using this pipeline
                </EmptyStateDescription>
                <Link
                  to={`/team/${teamId}/pipelines/${pipelineId}/configuration`}
                >
                  <Button type="primary" icon={<SettingOutlined />}>
                    Configure Pipeline
                  </Button>
                </Link>
              </EmptyStateSection>
            </ConfigSection>
          )}
        </SectionContainer>
      </DetailsPageLayout.MainContent>,

      <DetailsPageLayout.MainContent
        key="main-content-executions"
        title="Recent Executions"
      >
        {recentExecutions.length > 0 ? (
          <Table
            columns={executionColumns}
            dataSource={recentExecutions}
            rowKey="pipelineExecutionId"
            pagination={false}
            size="small"
            onRow={(record) => ({
              onClick: () => {
                void navigate(
                  `/team/${teamId}/pipelines/${pipelineId}/executions/${record.pipelineExecutionId}`,
                );
              },
              style: { cursor: 'pointer' },
            })}
          />
        ) : (
          <EmptyStateSection>
            <EmptyStateTitle level={5}>No executions yet</EmptyStateTitle>
            <EmptyStateDescription type="secondary">
              Run your pipeline to see execution history
            </EmptyStateDescription>
            <Space>
              <Link to={`/team/${teamId}/pipelines/${pipelineId}/workbench`}>
                <Button type="primary" icon={<ExperimentOutlined />}>
                  Go to Workbench
                </Button>
              </Link>
            </Space>
          </EmptyStateSection>
        )}
      </DetailsPageLayout.MainContent>,
    ];
  };

  return (
    <div>
      <DetailsPageLayout {...layoutProps}>{renderContent()}</DetailsPageLayout>

      {teamId && (
        <CreatePipelineModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          teamId={teamId}
          onSuccess={(newPipelineId) => {
            void navigate(`/team/${teamId}/pipelines/${newPipelineId}`);
          }}
        />
      )}
    </div>
  );
};
